import type {
  CoachDevelopmentAllocation,
  CoachDevelopmentPlan,
  CoachProfile,
  StaffMember,
} from '../game/types'

interface StaffDevelopmentProps {
  seniorCoach: CoachProfile
  assistants: StaffMember[]
  plan: CoachDevelopmentPlan
  onUpdate: (plan: CoachDevelopmentPlan) => void
  onComplete: () => void
  state: import('../game/types').GameState
}

const MAX_POINTS_PER_COACH = 3

const investmentLabel = (points: number) => {
  if (points === 0) return 'No formal investment'
  if (points === 1) return 'Foundation program'
  if (points === 2) return 'Focused development'
  return 'Elite marginal gain — diminishing returns'
}

export function StaffDevelopment({ seniorCoach, assistants, plan, onUpdate, onComplete, state }: StaffDevelopmentProps) {
  const coaches: CoachProfile[] = [seniorCoach, ...assistants]
  const used = plan.allocations.reduce((sum, allocation) => sum + allocation.points, 0)
  const remaining = plan.totalPoints - used

  const allocationFor = (coach: CoachProfile): CoachDevelopmentAllocation =>
    plan.allocations.find(allocation => allocation.coachId === coach.id) ?? {
      coachId: coach.id,
      focus: coach.developmentFocuses[0],
      points: 0,
    }

  const replaceAllocation = (nextAllocation: CoachDevelopmentAllocation) => {
    const hasAllocation = plan.allocations.some(allocation => allocation.coachId === nextAllocation.coachId)
    onUpdate({
      ...plan,
      allocations: hasAllocation
        ? plan.allocations.map(allocation => allocation.coachId === nextAllocation.coachId ? nextAllocation : allocation)
        : [...plan.allocations, nextAllocation],
    })
  }

  const movePoint = (coach: CoachProfile, change: -1 | 1) => {
    const current = allocationFor(coach)
    const nextPoints = current.points + change
    if (plan.isLocked || nextPoints < 0 || nextPoints > MAX_POINTS_PER_COACH || (change > 0 && remaining === 0)) return
    replaceAllocation({ ...current, points: nextPoints })
  }

  const changeFocus = (coach: CoachProfile, focus: string) => {
    if (plan.isLocked) return
    replaceAllocation({ ...allocationFor(coach), focus })
  }

  const lockPlan = () => {
    if (plan.isLocked) {
      onComplete()
      return
    }
    if (remaining !== 0) return
    onUpdate({ ...plan, isLocked: true })
    onComplete()
  }

  return (
    <div className="screen coach-development-screen">
      <div className="page-title">
        <div>
          <span className="eyebrow">Pre-season — annual decision</span>
          <h1>Coach Development</h1>
          <p>Allocate one shared pool between your own permanent career growth and specialist assistant development. The plan locks for the full season.</p>
        </div>
        <span className={`status-pill ${plan.isLocked ? '' : 'override'}`}>
                  {plan.isLocked ? `Locked for Season ${plan.season}` : `Season ${plan.season} planning`}
                </span>
              </div>

              {plan.season > 1 && state.recruitment && (
                <section className="offseason-summary panel" aria-label="Previous off-season summary">
                  <span className="eyebrow">Previous off-season</span>
                  <h2>Season {plan.season - 1} → {plan.season} transition</h2>
                  <div className="offseason-stats">
                    {state.recruitment.tradeOffers.filter(o => o.status === 'accepted').length > 0 && (
                      <span>Trades: <strong>{state.recruitment.tradeOffers.filter(o => o.status === 'accepted').length}</strong> accepted, <strong>{state.recruitment.acquiredPicks.length}</strong> picks acquired</span>
                    )}
                    {state.recruitment.draft.order.filter(pick => pick.clubId === state.season?.userTeamId && pick.prospectId).length > 0 && (
                      <span>Draft: <strong>{state.recruitment.draft.order.filter(pick => pick.clubId === state.season?.userTeamId && pick.prospectId).length}</strong> selections</span>
                    )}
                    {state.recruitment.delistedPlayerIds.length > 0 && (
                      <span>Delistings: <strong>{state.recruitment.delistedPlayerIds.length}</strong> players removed</span>
                    )}
                    <span>Final list: <strong>{state.club.players.length} of 24</strong></span>
                  </div>
                </section>
              )}

              <section className="coach-development-summary panel" aria-label="Coach development allocation summary">
        <div>
          <span>Annual pool</span>
          <strong>{used} of {plan.totalPoints} allocated</strong>
        </div>
        <div>
          <span>Remaining</span>
          <strong>{remaining}</strong>
        </div>
        <p><strong>Trade-off:</strong> investing in yourself creates permanent career capital. Developing assistants creates broader specialist leverage, but increases their market value and poaching risk.</p>
      </section>

      <section className="coach-identity panel">
        <div>
          <span className="eyebrow">Your ten-year career</span>
          <h2>Competent. Unproven. No chosen archetype.</h2>
          <p>Abilities improve through pre-season investment and use. Characteristics are earned from repeated behaviour and results; they cannot be purchased.</p>
        </div>
        <div>
          <span>Current characteristics</span>
          <strong>{seniorCoach.characteristics.length ? seniorCoach.characteristics.join(' · ') : 'No defining characteristics yet'}</strong>
          <small>Emerging tendencies will appear after enough evidence exists.</small>
        </div>
      </section>

      <div className="coach-development-grid">
        {coaches.map(coach => {
          const allocation = allocationFor(coach)
          const projectedPoachability = Math.min(95, coach.poachability + allocation.points * 6)
          return (
            <article className={`coach-card panel ${coach.isSeniorCoach ? 'senior-coach-card' : ''}`} key={coach.id}>
              <header>
                <div><span className="eyebrow">{coach.role}</span><h2>{coach.name}</h2></div>
                <span className="coach-reputation">{coach.reputation}</span>
              </header>

              <div className="coach-context">
                <span><small>Teaching style</small><strong>{coach.teachingStyle}</strong></span>
                <span><small>Tactical fit</small><strong>{coach.tacticalAlignment}</strong></span>
                <span><small>Relationships</small><strong>{coach.relationships}</strong></span>
                <span><small>Workload</small><strong>{coach.workload}%</strong></span>
              </div>

              <div className="coach-abilities">
                {Object.entries(coach.abilities).map(([ability, grade]) => (
                  <div key={ability}><span>{ability}</span><strong>{grade}</strong></div>
                ))}
              </div>

              <label className="coach-focus">
                <span>Season development focus</span>
                <select
                  aria-label={`Development focus for ${coach.name}`}
                  disabled={plan.isLocked}
                  value={allocation.focus}
                  onChange={event => changeFocus(coach, event.target.value)}
                >
                  {coach.developmentFocuses.map(focus => <option key={focus}>{focus}</option>)}
                </select>
              </label>

              <div className="coach-development-stepper">
                <button
                  type="button"
                  aria-label={`Remove one development point from ${coach.name}`}
                  disabled={plan.isLocked || allocation.points === 0}
                  onClick={() => movePoint(coach, -1)}
                >−</button>
                <div><strong>{allocation.points}</strong><span>annual points</span></div>
                <button
                  type="button"
                  aria-label={`Add one development point to ${coach.name}`}
                  disabled={plan.isLocked || remaining === 0 || allocation.points === MAX_POINTS_PER_COACH}
                  onClick={() => movePoint(coach, 1)}
                >+</button>
              </div>

              <div className="coach-investment-effect">
                <strong>{investmentLabel(allocation.points)}</strong>
                <span>{allocation.points ? `Expected progress in ${allocation.focus}; improvement still requires relevant experience.` : 'Existing ability remains available; no targeted progression bonus.'}</span>
              </div>

              <footer>
                {coach.isSeniorCoach ? (
                  <><span>Career ownership</span><strong>Permanent — follows you between clubs</strong></>
                ) : (
                  <><span>Projected poachability</span><strong>{coach.poachability}% → {projectedPoachability}%</strong></>
                )}
              </footer>
            </article>
          )
        })}
      </div>

      <div className="sticky-actions">
        <span>{plan.isLocked ? 'Annual plan locked — no weekly coach-development decisions' : remaining === 0 ? 'Complete plan ready to lock' : `${remaining} development point${remaining === 1 ? '' : 's'} still unallocated`}</span>
        <button className="primary-action" type="button" disabled={remaining !== 0} onClick={lockPlan}>
          {plan.isLocked ? state.preseasonComplete === false ? 'Return to Pre-season Hub' : 'Return to Week' : `Lock Season ${plan.season} Plan — Return to ${state.preseasonComplete === false ? 'Pre-season Hub' : 'Week'}`}
        </button>
      </div>
    </div>
  )
}
